import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  Banknote,
  CreditCard,
  Receipt,
  LogOut,
  Search,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  Link2,
  Copy,
  HelpCircle,
} from "lucide-react";
import { PosHelpModal } from "@/components/StaffHelp";
import RoleWalkthrough from "@/components/RoleWalkthrough";
import { toast } from "sonner";

const POS_STAFF_ROLES = ["admin", "owner", "manager", "associate", "cashier"];

/**
 * POS — In-store counter sale terminal.
 *
 * Accessible to admin OR cashier role. Built to be usable on every device:
 * phone (touch), tablet (split view), desktop (full grid + keyboard).
 *
 * Flow:
 *   1. Search/select products → cart pane fills + total
 *   2. Cash → enter amount tendered → see change due → record
 *   3. Card → open Square POS app via deep-link → return marks order paid
 */
export default function POSPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // [{product, qty}]
  const [paying, setPaying] = useState(null); // null | "cash" | "card"
  const [tendered, setTendered] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [linkSent, setLinkSent] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [todayStats, setTodayStats] = useState({ today_count: 0, today_total: 0 });
  const [completed, setCompleted] = useState(null); // last sale receipt
  const [discount, setDiscount] = useState(null); // {pct, token, approvedBy}
  const [discountPane, setDiscountPane] = useState(false);
  const [discountPctInput, setDiscountPctInput] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [pinNeeded, setPinNeeded] = useState(false);
  const [pinEmail, setPinEmail] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const searchInputRef = useRef(null);

  // Redirect non-staff away
  useEffect(() => {
    if (user && !POS_STAFF_ROLES.includes(user.role)) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Load product catalog (only active items in stock)
  useEffect(() => {
    api
      .get("/products?limit=500")
      .then((r) => {
        const items = (r.data.items || r.data || []).filter(
          (p) => p.is_active !== false && (p.stock ?? 1) > 0 && !p.is_pallet
        );
        setProducts(items);
      })
      .catch(() => toast.error("Couldn't load product catalog"));
  }, []);

  // Load today's tape
  const loadTape = () =>
    api
      .get("/pos/recent")
      .then((r) =>
        setTodayStats({
          today_count: r.data.today_count || 0,
          today_total: r.data.today_total || 0,
        })
      )
      .catch(() => {});
  useEffect(() => {
    loadTape();
  }, []);

  // Auto-focus search
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // ── Cart helpers ────────────────────────────────────────────────────────
  const addToCart = (product) => {
    setCart((prev) => {
      const found = prev.find((c) => c.product.id === product.id);
      if (found) {
        return prev.map((c) =>
          c.product.id === product.id ? { ...c, qty: c.qty + 1 } : c
        );
      }
      return [...prev, { product, qty: 1 }];
    });
  };
  const removeFromCart = (id) =>
    setCart((prev) => prev.filter((c) => c.product.id !== id));
  const setQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setCart((prev) =>
      prev.map((c) => (c.product.id === id ? { ...c, qty } : c))
    );
  };
  const clearCart = () => {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setTendered("");
    setPaying(null);
    setDiscount(null);
    setDiscountPane(false);
    setDiscountPctInput("");
    setDiscountReason("");
    setPinNeeded(false);
  };

  // ── Totals (matches backend math: subtotal × 6% tax, no member discount) ─
  const subtotal = cart.reduce((s, c) => s + c.product.price * c.qty, 0);
  const discountAmount = discount ? +((subtotal * discount.pct) / 100).toFixed(2) : 0;
  const tax = +((subtotal - discountAmount) * 0.06).toFixed(2);
  const total = +(subtotal - discountAmount + tax).toFixed(2);
  const tenderedNum = parseFloat(tendered) || 0;
  const changeDue = +(tenderedNum - total).toFixed(2);

  // ── Discount authority (mirrors server role_can_discount) ─────────────
  const cartQty = cart.reduce((s, c) => s + c.qty, 0);
  const isBundle = cartQty >= 3;
  const myDiscountLimit =
    user?.role === "owner" || user?.role === "admin"
      ? 100
      : user?.role === "manager"
        ? (isBundle ? 15 : 10)
        : 0;

  const applyDiscount = () => {
    const pct = parseFloat(discountPctInput) || 0;
    if (pct <= 0 || pct > 100) return toast.error("Enter a percent between 1 and 100");
    if (pct <= myDiscountLimit) {
      setDiscount({ pct, token: null, approvedBy: user?.email });
      setDiscountPane(false);
      setPinNeeded(false);
      toast.success(`${pct}% discount applied`);
    } else {
      setPinNeeded(true);
    }
  };

  const verifyManagerPin = async () => {
    const pct = parseFloat(discountPctInput) || 0;
    if (!pinEmail || pinCode.length !== 4) return toast.error("Manager email + 4-digit PIN required");
    setPinBusy(true);
    try {
      const { data } = await api.post("/pos/verify-pin", {
        manager_email: pinEmail,
        pin: pinCode,
        reason: discountReason || `POS discount ${pct}%`,
        target_type: "pos_sale",
        discount_percent: pct,
        is_bundle: isBundle,
      });
      setDiscount({ pct, token: data.approval_token, approvedBy: data.manager?.email });
      setPinNeeded(false);
      setDiscountPane(false);
      setPinCode("");
      toast.success(`Manager approved ${pct}% — complete the sale within 90 seconds`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PIN rejected");
    } finally {
      setPinBusy(false);
    }
  };

  const discountPayload = () => ({
    discount_percent: discount?.pct || null,
    discount_reason: discountReason || null,
    approval_token: discount?.token || null,
  });

  // ── Search filter ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return products.slice(0, 60);
    const q = search.toLowerCase();
    return products
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.sku || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q)
      )
      .slice(0, 60);
  }, [products, search]);

  // ── Cash sale ──────────────────────────────────────────────────────────
  const submitCash = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (tenderedNum < total) return toast.error("Cash tendered is less than total");
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/cash", {
        items: cart.map((c) => ({ product_id: c.product.id, quantity: c.qty })),
        amount_tendered: tenderedNum,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        ...discountPayload(),
      });
      setCompleted(data);
      clearCart();
      loadTape();
      toast.success(`Sale ${data.order_number} complete`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cash sale failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment link (text/email) ──────────────────────────────────────────
  const submitLink = async () => {
    if (!customerName.trim()) return toast.error("Customer name is required");
    if (!customerPhone && !customerEmail) return toast.error("Add a phone or email to send the link");
    setSubmitting(true);
    try {
      const { data } = await api.post("/paylinks", {
        items: cart.map((c) => ({ product_id: c.product.id, quantity: c.qty })),
        customer_name: customerName.trim(),
        customer_phone: customerPhone || null,
        customer_email: customerEmail || null,
      });
      setLinkSent(data);
      toast.success("Payment link sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send link");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Card via Square POS deep-link ──────────────────────────────────────
  const submitCard = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/card/intent", {
        items: cart.map((c) => ({ product_id: c.product.id, quantity: c.qty })),
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        ...discountPayload(),
      });
      toast.info("Opening Square POS app — customer taps card now");
      // Open the deep link. Square POS handles tap-to-pay and redirects back.
      window.location.href = data.deep_link;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Card intent failed");
      setSubmitting(false);
    }
  };

  // ── Receipt view (after a sale) ────────────────────────────────────────
  if (completed) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-start justify-center">
        <div
          className="bg-white max-w-md w-full p-8 border-2 border-[#0A1C40]"
          data-testid="pos-receipt"
        >
          <div className="text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600" strokeWidth={2.5} />
            <h2 className="font-display font-black text-3xl mt-3">Sale Complete</h2>
            <div className="text-sm text-gray-600 mt-1">
              Order <span className="font-mono font-bold">{completed.order_number}</span>
            </div>
          </div>
          <div className="mt-6 border-t-2 border-dashed border-gray-300 pt-4 space-y-2 text-sm">
            {completed.line_items.map((li, i) => (
              <div key={i} className="flex justify-between">
                <span>
                  {li.name} × {li.quantity}
                </span>
                <span className="font-bold">{fmtMoney(li.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-300 space-y-1 text-sm">
            {completed.discount_amount > 0 && (
              <div className="flex justify-between text-green-700 font-bold">
                <span>Discount ({completed.discount_percent}%)</span>
                <span data-testid="pos-receipt-discount">−{fmtMoney(completed.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Total</span>
              <span className="font-bold">{fmtMoney(completed.total)}</span>
            </div>
            <div className="flex justify-between">
              <span>{completed.payment_method === "cash" ? "Cash tendered" : "Paid by card"}</span>
              <span className="font-bold">{fmtMoney(completed.amount_tendered || completed.total)}</span>
            </div>
            {completed.change_due > 0 && (
              <div className="flex justify-between text-[#DC2626] text-xl font-display font-black mt-2 pt-2 border-t border-gray-300">
                <span>CHANGE DUE</span>
                <span data-testid="pos-receipt-change">{fmtMoney(completed.change_due)}</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setCompleted(null)}
            className="mt-8 w-full bg-[#0A1C40] text-white font-bold py-3 rounded-full hover:bg-black transition-colors"
            data-testid="pos-new-sale-btn"
          >
            New Sale →
          </button>
        </div>
      </div>
    );
  }

  // ── Main POS UI ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50" data-testid="pos-page">
      {/* Top bar */}
      <header className="bg-[#0A1C40] text-white px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Link to="/" className="opacity-80 hover:opacity-100" data-testid="pos-back-home">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="font-display font-black text-lg">IPVALUE POS</div>
          <span className="hidden md:inline text-xs opacity-70">
            {user?.name || user?.email} · {user?.role}
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Today</div>
            <div className="font-bold">
              {todayStats.today_count} sale{todayStats.today_count === 1 ? "" : "s"} ·{" "}
              <span className="text-[#FFC220]" data-testid="pos-today-total">
                {fmtMoney(todayStats.today_total)}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowHelp(true)}
            className="flex items-center gap-1 text-xs font-bold bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded-full"
            data-testid="pos-help-btn"
            title="How to use the register"
          >
            <HelpCircle className="w-4 h-4" /> Help
          </button>
          <button
            onClick={logout}
            className="text-xs underline opacity-80 hover:opacity-100"
            data-testid="pos-logout-btn"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      {showHelp && <PosHelpModal onClose={() => setShowHelp(false)} />}
      <RoleWalkthrough />

      <div className="grid lg:grid-cols-[1fr_420px] gap-0 lg:gap-4 lg:p-4 max-w-[1600px] mx-auto">
        {/* Product grid */}
        <section className="bg-white lg:rounded p-4">
          <div className="flex items-center gap-2 border-b-2 border-[#0A1C40] pb-2 mb-3">
            <Search className="w-4 h-4 text-[#0A1C40]" />
            <input
              ref={searchInputRef}
              className="flex-1 outline-none text-sm py-1"
              placeholder="Search by name, SKU, or category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pos-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-xs text-gray-500">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              No matching products. Try a different search.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => addToCart(p)}
                  className="text-left bg-white border border-gray-200 hover:border-[#0A1C40] hover:shadow-md transition-all p-2 group"
                  data-testid={`pos-product-${p.id}`}
                >
                  <div className="aspect-square bg-gray-100 mb-2 overflow-hidden">
                    {p.images?.[0] ? (
                      <img
                        src={fileUrl(p.images[0])}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                        no photo
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] font-bold truncate leading-tight">{p.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {p.sku} · stock {p.stock}
                  </div>
                  <div className="font-display font-black text-base text-[#0A1C40]">
                    {fmtMoney(p.price)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Cart pane */}
        <aside className="bg-white lg:rounded p-4 lg:max-h-[calc(100vh-100px)] flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="font-display font-black text-lg">Cart</h2>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="ml-auto text-xs text-red-600 underline"
                data-testid="pos-clear-cart"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 mb-3">
            {cart.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-10">
                Tap a product to add it
              </div>
            ) : (
              cart.map((c) => (
                <div
                  key={c.product.id}
                  className="border border-gray-200 p-2 flex gap-2 items-center"
                  data-testid={`pos-cart-line-${c.product.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">{c.product.name}</div>
                    <div className="text-[10px] text-gray-500">
                      {fmtMoney(c.product.price)} ea
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setQty(c.product.id, c.qty - 1)}
                      className="w-6 h-6 border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-bold">{c.qty}</span>
                    <button
                      onClick={() => setQty(c.product.id, c.qty + 1)}
                      className="w-6 h-6 border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-bold text-sm w-16 text-right">
                    {fmtMoney(c.product.price * c.qty)}
                  </div>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <>
              {/* Customer info (optional) */}
              <details className="mb-3">
                <summary className="text-xs text-gray-600 cursor-pointer">
                  + Customer info (optional, for receipt)
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    className="input-base text-sm"
                    placeholder="Customer name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    data-testid="pos-customer-name"
                  />
                  <input
                    className="input-base text-sm"
                    placeholder="Phone (for SMS receipt)"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    data-testid="pos-customer-phone"
                  />
                  <input
                    className="input-base text-sm"
                    placeholder="Email (for payment link / receipt)"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    data-testid="pos-customer-email"
                  />
                </div>
              </details>

              {/* Totals */}
              <div className="border-t-2 border-[#0A1C40] pt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span data-testid="pos-subtotal">{fmtMoney(subtotal)}</span>
                </div>
                {discount && (
                  <div className="flex justify-between text-green-700 font-bold" data-testid="pos-discount-row">
                    <span>
                      Discount ({discount.pct}%)
                      <button
                        onClick={() => setDiscount(null)}
                        className="ml-2 text-[10px] text-red-600 underline"
                        data-testid="pos-remove-discount"
                      >
                        remove
                      </button>
                    </span>
                    <span>−{fmtMoney(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Tax (6%)</span>
                  <span>{fmtMoney(tax)}</span>
                </div>
                <div className="flex justify-between font-display font-black text-2xl pt-1">
                  <span>Total</span>
                  <span data-testid="pos-total">{fmtMoney(total)}</span>
                </div>
                {!discount && !discountPane && !paying && (
                  <button
                    onClick={() => setDiscountPane(true)}
                    className="text-xs text-[#0A1C40] underline font-bold"
                    data-testid="pos-add-discount"
                  >
                    + Add discount
                  </button>
                )}
              </div>

              {/* Discount pane */}
              {discountPane && !paying && (
                <div className="mt-3 p-3 border-2 border-green-700 bg-green-50" data-testid="pos-discount-pane">
                  <div className="text-xs font-bold mb-1">Discount ({isBundle ? "bundle — 3+ items" : "single item"})</div>
                  {myDiscountLimit === 0 ? (
                    <div className="text-xs bg-amber-100 border border-amber-500 text-amber-900 px-2 py-1.5 font-semibold mb-2" data-testid="pos-discount-blocked-note">
                      ⚠ Your role can't apply discounts. Enter the percent, then call a manager over — they approve it with their 4-digit PIN right here.
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-600 mb-2">
                      You can approve up to <b>{myDiscountLimit}%</b> yourself. Bigger discounts need a manager PIN.
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      className="input-base !w-20 text-sm"
                      placeholder="%"
                      value={discountPctInput}
                      onChange={(e) => { setDiscountPctInput(e.target.value); setPinNeeded(false); }}
                      data-testid="pos-discount-pct"
                    />
                    <input
                      className="input-base flex-1 text-sm"
                      placeholder="Reason (e.g. floor model scratch)"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      data-testid="pos-discount-reason"
                    />
                  </div>
                  {pinNeeded && (
                    <div className="mt-2 p-2 bg-white border border-amber-500" data-testid="pos-pin-modal">
                      <div className="text-[11px] font-bold text-amber-900 mb-1.5">
                        Manager approval required for {discountPctInput}%
                      </div>
                      <div className="flex gap-2">
                        <input
                          className="input-base flex-1 text-sm"
                          type="email"
                          placeholder="Manager email"
                          value={pinEmail}
                          onChange={(e) => setPinEmail(e.target.value)}
                          data-testid="pos-pin-email"
                        />
                        <input
                          className="input-base !w-24 text-sm text-center tracking-[0.3em]"
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="PIN"
                          value={pinCode}
                          onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          data-testid="pos-pin-input"
                        />
                      </div>
                      <button
                        onClick={verifyManagerPin}
                        disabled={pinBusy}
                        className="mt-2 w-full bg-amber-600 text-white text-xs font-bold py-2 rounded-full disabled:opacity-50"
                        data-testid="pos-pin-verify"
                      >
                        {pinBusy ? "Verifying…" : "Approve with manager PIN"}
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      onClick={() => { setDiscountPane(false); setPinNeeded(false); }}
                      className="border border-gray-400 text-gray-700 text-xs font-bold py-1.5 rounded-full"
                      data-testid="pos-discount-cancel"
                    >
                      Cancel
                    </button>
                    {!pinNeeded && (
                      <button
                        onClick={applyDiscount}
                        className="bg-green-700 text-white text-xs font-bold py-1.5 rounded-full"
                        data-testid="pos-discount-apply"
                      >
                        Apply
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Payment buttons */}
              {!paying && (
                <>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button
                    onClick={() => setPaying("cash")}
                    className="bg-[#FFC220] text-[#0A1C40] font-bold py-4 rounded-full hover:bg-[#FFB300] transition-colors flex flex-col items-center gap-1"
                    data-testid="pos-pay-cash"
                  >
                    <Banknote className="w-6 h-6" />
                    Cash
                  </button>
                  <button
                    onClick={() => setPaying("card")}
                    className="bg-[#0A1C40] text-white font-bold py-4 rounded-full hover:bg-black transition-colors flex flex-col items-center gap-1"
                    data-testid="pos-pay-card"
                  >
                    <CreditCard className="w-6 h-6" />
                    Card · Tap
                  </button>
                </div>
                <button
                  onClick={() => { setPaying("link"); setLinkSent(null); }}
                  className="mt-2 w-full border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2.5 rounded-full hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                  data-testid="pos-pay-link"
                >
                  <Link2 className="w-4 h-4" /> Send payment link (text / email)
                </button>
                </>
              )}

              {/* Payment link pane */}
              {paying === "link" && (
                <div className="mt-4 p-3 border-2 border-[#0A1C40] bg-blue-50" data-testid="pos-link-pane">
                  {!linkSent ? (
                    <>
                      <p className="text-sm font-bold">Send a secure pay-by-phone link</p>
                      <p className="text-xs text-gray-600 mt-1">Customer pays this exact cart from their phone. Link expires in 48 h. Needs name + phone or email.</p>
                      <div className="space-y-2 mt-2">
                        <input className="input-base text-sm" placeholder="Customer name *" value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="pos-link-name" />
                        <input className="input-base text-sm" placeholder="Phone (texts the link)" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} data-testid="pos-link-phone" />
                        <input className="input-base text-sm" type="email" placeholder="Email (emails the link)" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} data-testid="pos-link-email" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <button onClick={() => setPaying(null)} className="border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2 rounded-full hover:bg-gray-100">Cancel</button>
                        <button onClick={submitLink} disabled={submitting}
                          className="bg-[#0A1C40] text-white font-bold py-2 rounded-full hover:bg-black disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="pos-send-link">
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-4 h-4" /> Send link</>}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div data-testid="pos-link-sent">
                      <p className="text-sm font-bold text-green-700">✓ Link sent — order {linkSent.order_number} ({fmtMoney(linkSent.total)})</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {linkSent.sms_queued && "Texted. "}{linkSent.email_queued && "Emailed. "}Expires in 48 h. Watch it flip to PAID in Admin → Orders.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <input readOnly className="input-base text-xs flex-1" value={linkSent.url} data-testid="pos-link-url" />
                        <button onClick={() => { navigator.clipboard.writeText(linkSent.url); toast.success("Copied"); }}
                          className="border border-gray-300 px-3 text-xs" data-testid="pos-link-copy"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => { setPaying(null); setLinkSent(null); clearCart(); }}
                        className="mt-3 w-full bg-[#0A1C40] text-white font-bold py-2 rounded-full" data-testid="pos-link-done">
                        Done — new sale
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Cash modal (inline) */}
              {paying === "cash" && (
                <div className="mt-4 p-3 border-2 border-[#FFC220] bg-[#FFF8E1]" data-testid="pos-cash-pane">
                  <div className="text-xs font-bold mb-1">Amount tendered</div>
                  <input
                    className="input-base text-2xl font-display font-black"
                    type="number"
                    step="0.01"
                    value={tendered}
                    onChange={(e) => setTendered(e.target.value)}
                    autoFocus
                    placeholder="0.00"
                    data-testid="pos-tendered-input"
                  />
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    {[total, Math.ceil(total / 10) * 10, Math.ceil(total / 20) * 20, Math.ceil(total / 50) * 50, 100, 200].slice(0, 4).map((preset, i) => (
                      <button
                        key={i}
                        onClick={() => setTendered(String(preset))}
                        className="text-xs bg-white border border-gray-300 py-1 hover:bg-gray-100"
                      >
                        {fmtMoney(preset)}
                      </button>
                    ))}
                  </div>
                  {tenderedNum >= total && (
                    <div className="mt-3 flex justify-between text-[#DC2626] font-display font-black text-xl">
                      <span>Change due</span>
                      <span data-testid="pos-change-due">{fmtMoney(changeDue)}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button
                      onClick={() => {
                        setPaying(null);
                        setTendered("");
                      }}
                      className="border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2 rounded-full hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitCash}
                      disabled={submitting || tenderedNum < total}
                      className="bg-[#0A1C40] text-white font-bold py-2 rounded-full hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="pos-confirm-cash"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Receipt className="w-4 h-4" /> Complete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Card pane */}
              {paying === "card" && (
                <div className="mt-4 p-3 border-2 border-[#0A1C40] bg-gray-50" data-testid="pos-card-pane">
                  <div className="text-sm">
                    <p className="font-bold">Tap to Pay via Square POS</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Press the button below. The Square POS app will open with this
                      amount pre-loaded. Hand the device to your customer to tap their
                      card or phone.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button
                      onClick={() => setPaying(null)}
                      className="border-2 border-[#0A1C40] text-[#0A1C40] font-bold py-2 rounded-full hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitCard}
                      disabled={submitting}
                      className="bg-[#0A1C40] text-white font-bold py-2 rounded-full hover:bg-black disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="pos-confirm-card"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" /> Open Square POS
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
